<template>
  <NavbarTwo />

  <div class="wc-hero">
    <div class="container mw-1356">
      <div class="wc-hero-inner">
        <div class="wc-badge">FIFA World Cup 2026</div>
        <h1>Watch Every World Cup 2026 Match <span class="wc-red">Live in HD</span></h1>
        <p>All 104 matches. 48 teams. 3 countries. Stream every kick-off live on any device with goplayTv — from the Group Stage to the Final.</p>
        <div class="wc-hero-actions">
          <RouterLink to="/pricing" class="btn wc-btn-primary">
            <span class="border-style">Get Started — From $10/mo</span>
          </RouterLink>
          <RouterLink to="/channels" class="btn">
            <span class="border-style">Browse Channels</span>
          </RouterLink>
        </div>
        <div class="wc-stats-row">
          <div class="wc-stat"><span>104</span><p>Matches</p></div>
          <div class="wc-stat"><span>48</span><p>Teams</p></div>
          <div class="wc-stat"><span>25,000+</span><p>Channels</p></div>
          <div class="wc-stat"><span>4K</span><p>Quality</p></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Why goplayTv for World Cup -->
  <div class="wc-section">
    <div class="container mw-1356">
      <h2 class="wc-section-title">Why Watch World Cup 2026 on goplayTv?</h2>
      <div class="wc-features-grid">
        <div class="wc-feature-card" v-for="f in features" :key="f.title">
          <i :class="f.icon"></i>
          <h3>{{ f.title }}</h3>
          <p>{{ f.desc }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Schedule / Groups -->
  <div class="wc-section wc-dark">
    <div class="container mw-1356">
      <h2 class="wc-section-title">Tournament Overview</h2>
      <div class="wc-schedule-grid">
        <div class="wc-schedule-card" v-for="phase in phases" :key="phase.name">
          <div class="wc-phase-badge">{{ phase.dates }}</div>
          <h4>{{ phase.name }}</h4>
          <p>{{ phase.desc }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Channels -->
  <div class="wc-section">
    <div class="container mw-1356">
      <h2 class="wc-section-title">Channels Broadcasting World Cup 2026</h2>
      <p class="wc-section-sub">goplayTv carries every major broadcaster so you never miss a match regardless of your location.</p>
      <div class="wc-channels-grid">
        <div class="wc-channel-tag" v-for="ch in channels" :key="ch">{{ ch }}</div>
      </div>
    </div>
  </div>

  <!-- How to watch -->
  <div class="wc-section wc-dark">
    <div class="container mw-1356">
      <h2 class="wc-section-title">How to Watch in 3 Steps</h2>
      <div class="wc-steps-row">
        <div class="wc-step">
          <div class="wc-step-num">1</div>
          <h4>Choose a Plan</h4>
          <p>Pick any subscription from $10/month. Instant activation.</p>
        </div>
        <div class="wc-step-arrow"><i class="ti ti-arrow-right"></i></div>
        <div class="wc-step">
          <div class="wc-step-num">2</div>
          <h4>Get Your Line</h4>
          <p>Receive your M3U/credentials via email within minutes.</p>
        </div>
        <div class="wc-step-arrow"><i class="ti ti-arrow-right"></i></div>
        <div class="wc-step">
          <div class="wc-step-num">3</div>
          <h4>Watch Live</h4>
          <p>Load on Smart TV, phone, tablet, Firestick, or PC and enjoy.</p>
        </div>
      </div>
      <div class="text-center mt-5">
        <RouterLink to="/pricing" class="btn wc-btn-primary">
          <span class="border-style">Subscribe Now &rarr;</span>
        </RouterLink>
      </div>
    </div>
  </div>

  <!-- FAQ -->
  <div class="wc-section">
    <div class="container mw-1356" style="max-width:800px">
      <h2 class="wc-section-title">World Cup 2026 FAQ</h2>
      <div class="wc-faq-list">
        <div class="wc-faq-item" v-for="(q, i) in faqs" :key="i" @click="openFaq = openFaq === i ? -1 : i">
          <div class="wc-faq-q">
            <span>{{ q.q }}</span>
            <i :class="openFaq === i ? 'ti ti-chevron-up' : 'ti ti-chevron-down'"></i>
          </div>
          <div class="wc-faq-a" v-if="openFaq === i">{{ q.a }}</div>
        </div>
      </div>
    </div>
  </div>

  <MainFooter class="bg-black" />
  <CopyRight />
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import NavbarTwo from "@/components/Layouts/NavbarTwo.vue";
import MainFooter from "@/components/Layouts/MainFooter.vue";
import CopyRight from "@/components/Layouts/CopyRight.vue";
import { useSeo } from "@/composables/useSeo";

export default defineComponent({
  name: "WorldCupPage",
  components: { NavbarTwo, MainFooter, CopyRight },
  setup() {
    useSeo({
      title: "Watch FIFA World Cup 2026 Live — All 104 Matches in HD",
      description: "Stream every FIFA World Cup 2026 match live in HD/4K with goplayTv IPTV. 104 matches, 48 teams, USA/Canada/Mexico. From $10/month. Watch on any device.",
      url: "/world-cup-2026",
      keywords: "watch World Cup 2026 live, FIFA 2026 IPTV, stream World Cup online, World Cup 2026 channels, live football 2026, IPTV World Cup",
    });

    const openFaq = ref(-1);

    const features = [
      { icon: "ti ti-device-tv", title: "All Broadcast Channels", desc: "Every TV network airing World Cup 2026 games is in our lineup — no match left uncovered." },
      { icon: "ti ti-4k", title: "HD & 4K Streams", desc: "Crystal-clear picture quality so you catch every goal, VAR moment, and celebration in full detail." },
      { icon: "ti ti-clock-24", title: "24/7 Live Coverage", desc: "Pre-match shows, half-time analysis, and post-match replays available around the clock." },
      { icon: "ti ti-devices", title: "Any Device", desc: "Smart TV, Android, iOS, Firestick, MAG box, PC — watch on whatever screen you prefer." },
      { icon: "ti ti-world", title: "All 3 Host Countries", desc: "USA, Canada, and Mexico-based channels all included so you get every local broadcast." },
      { icon: "ti ti-bolt", title: "Instant Activation", desc: "Subscribe, receive credentials, and be watching within minutes. No waiting, no hassle." },
    ];

    const phases = [
      { name: "Group Stage", dates: "Jun 11 – Jul 2, 2026", desc: "48 teams divided into 12 groups. Top 2 from each group + 8 best third-placed teams advance." },
      { name: "Round of 32", dates: "Jul 4 – Jul 7, 2026", desc: "32 teams compete in single-elimination knockout matches." },
      { name: "Round of 16", dates: "Jul 9 – Jul 12, 2026", desc: "The 16 surviving teams battle for a quarter-final spot." },
      { name: "Quarter-Finals", dates: "Jul 15 – Jul 16, 2026", desc: "Eight teams, four matches — only the best move on." },
      { name: "Semi-Finals", dates: "Jul 19 – Jul 20, 2026", desc: "Two showpiece semi-finals to determine the finalists." },
      { name: "The Final", dates: "Jul 19, 2026", desc: "The biggest sporting event on the planet. One team lifts the trophy." },
    ];

    const channels = [
      "BBC Sport", "ITV", "Fox Sports", "Telemundo", "TSN", "RDS", "beIN Sports",
      "Sky Sports", "DAZN", "ESPN", "TRT Spor", "MBC", "Canal+", "RAI Sport",
      "ARD", "ZDF", "TVE", "SIC", "TF1", "Azteca", "TV Azteca", "Univision",
    ];

    const faqs = [
      { q: "Can I watch all 104 World Cup 2026 matches on goplayTv?", a: "Yes. goplayTv carries every major broadcaster airing World Cup 2026, including Fox Sports, BBC, ITV, beIN Sports, Telemundo, and more — ensuring full coverage of all 104 matches from Group Stage to the Final." },
      { q: "Which devices can I use to watch World Cup 2026?", a: "You can watch on Smart TVs (Samsung, LG), Android TV boxes, Amazon Firestick, iOS and Android phones, tablets, MAG boxes, and Windows/Mac computers using IPTV players like IPTV Smarters, TiviMate, or VLC." },
      { q: "Is the stream quality HD for World Cup matches?", a: "Yes. Most World Cup matches are available in Full HD (1080p) and selected matches in 4K Ultra HD depending on your internet connection speed (recommended 25 Mbps for 4K)." },
      { q: "How quickly do I get access after subscribing?", a: "Typically within minutes. After payment is confirmed, you'll receive your IPTV credentials by email. Load them into your IPTV player and you're ready to watch." },
      { q: "What is the cheapest plan to watch World Cup 2026?", a: "Our Basic plan starts at $10/month and includes all sports channels broadcasting World Cup 2026. For the best experience including 4K streams, we recommend the Premium Sports plan." },
    ];

    return { features, phases, channels, faqs, openFaq };
  },
});
</script>

<style scoped>
.wc-hero {
  background: linear-gradient(135deg, #0a0102 0%, #1a0505 50%, #0a0102 100%);
  padding: 120px 0 80px;
  border-bottom: 1px solid #3a2526;
  position: relative;
  overflow: hidden;
}
.wc-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 70% 50%, rgba(201,14,14,0.12) 0%, transparent 70%);
  pointer-events: none;
}
.wc-hero-inner { max-width: 760px; }
.wc-badge {
  display: inline-block;
  background: rgba(201,14,14,0.2);
  border: 1px solid rgba(201,14,14,0.4);
  color: #c90e0e;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 0.35rem 1rem;
  border-radius: 20px;
  margin-bottom: 1.25rem;
}
.wc-hero h1 {
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 900;
  color: #fff;
  line-height: 1.15;
  margin-bottom: 1.25rem;
}
.wc-red { color: #c90e0e; }
.wc-hero p {
  font-size: 1.1rem;
  color: #c0c0c0;
  max-width: 620px;
  margin-bottom: 2rem;
  line-height: 1.7;
}
.wc-hero-actions { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 3rem; }
.wc-btn-primary .border-style { background: #c90e0e !important; border-color: #c90e0e !important; }
.wc-stats-row { display: flex; gap: 2.5rem; flex-wrap: wrap; }
.wc-stat span { display: block; font-size: 2rem; font-weight: 900; color: #c90e0e; }
.wc-stat p { color: #888; font-size: 0.85rem; margin: 0; }

.wc-section { padding: 80px 0; }
.wc-dark { background: #0d0203; }
.wc-section-title {
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 800;
  color: #fff;
  text-align: center;
  margin-bottom: 0.75rem;
}
.wc-section-sub { text-align: center; color: #888; margin-bottom: 2.5rem; }

.wc-features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
  margin-top: 2.5rem;
}
.wc-feature-card {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 12px;
  padding: 1.75rem;
  transition: border-color 0.2s;
}
.wc-feature-card:hover { border-color: #c90e0e; }
.wc-feature-card i { font-size: 2rem; color: #c90e0e; display: block; margin-bottom: 1rem; }
.wc-feature-card h3 { color: #fff; font-size: 1rem; font-weight: 700; margin-bottom: 0.5rem; }
.wc-feature-card p { color: #888; font-size: 0.88rem; margin: 0; line-height: 1.6; }

.wc-schedule-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.25rem;
  margin-top: 2.5rem;
}
.wc-schedule-card {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 10px;
  padding: 1.5rem;
}
.wc-phase-badge {
  font-size: 0.75rem;
  color: #c90e0e;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}
.wc-schedule-card h4 { color: #fff; font-weight: 700; margin-bottom: 0.4rem; font-size: 0.95rem; }
.wc-schedule-card p { color: #888; font-size: 0.82rem; margin: 0; line-height: 1.5; }

.wc-channels-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 2rem;
}
.wc-channel-tag {
  background: #201516;
  border: 1px solid #3a2526;
  color: #c0c0c0;
  font-size: 0.85rem;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  transition: all 0.2s;
}
.wc-channel-tag:hover { border-color: #c90e0e; color: #fff; }

.wc-steps-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 2.5rem;
}
.wc-step { text-align: center; max-width: 200px; }
.wc-step-num {
  width: 56px; height: 56px;
  background: #c90e0e;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem; font-weight: 900; color: #fff;
  margin: 0 auto 1rem;
}
.wc-step h4 { color: #fff; font-weight: 700; margin-bottom: 0.4rem; }
.wc-step p { color: #888; font-size: 0.85rem; margin: 0; }
.wc-step-arrow { color: #3a2526; font-size: 1.5rem; }

.wc-faq-list { margin-top: 2rem; display: flex; flex-direction: column; gap: 0.75rem; }
.wc-faq-item {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 10px;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  transition: border-color 0.2s;
}
.wc-faq-item:hover { border-color: #c90e0e; }
.wc-faq-q {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
}
.wc-faq-q i { color: #c90e0e; flex-shrink: 0; }
.wc-faq-a {
  color: #a0a0a0;
  font-size: 0.88rem;
  line-height: 1.7;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #3a2526;
}
</style>
